import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Eye, Copy, Trash, Martini } from '@phosphor-icons/react';

const CocktailManageUrls = () => {
  const [deployments, setDeployments] = useState([]);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/cocktail/deployments`, { withCredentials: true });
      setDeployments(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure? This will delete both the Heroku app and the Azure Front Door Resource Group.")) return;
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/cocktail/deployments/${id}`, { withCredentials: true });
      toast.success("Teardown initiated in background.");
      fetchHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete deployment");
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div>
        <h1 className="text-4xl font-black mb-2 text-[#FF0055]">MANAGE URLs</h1>
        <p className="text-slate-400 font-mono">View, manage, and delete your blended Cocktail deployments.</p>
      </div>
      
      <div className="border border-white/10 bg-card overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-white/10 bg-black/20">
              <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider">Date</th>
              <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider">Site Name</th>
              <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider">Azure / Heroku Accounts</th>
              <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider">Status</th>
              <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider">Heroku Origin URL</th>
              <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider">Azure Front Door URL</th>
              <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {deployments.map(dep => (
              <tr key={dep.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                <td className="p-4 text-sm text-slate-400 font-mono">{format(new Date(dep.created_at), 'yyyy-MM-dd HH:mm')}</td>
                <td className="p-4 font-mono text-sm font-bold text-white">
                  <Martini className="inline mr-2 text-[#FF0055]" />
                  {dep.site_name}
                </td>
                <td className="p-4 text-sm">
                  <div className="flex flex-col text-slate-300">
                    <span className="text-[#0078D4] text-xs font-bold">AZ: {dep.azure_label}</span>
                    <span className="text-[#8b60ed] text-xs font-bold">HK: {dep.heroku_label}</span>
                  </div>
                </td>
                <td className="p-4 text-sm">
                  <span className={`px-2 py-1 text-xs font-bold tracking-wider ${
                    dep.status === 'live' ? 'bg-[#39FF14]/10 text-[#39FF14] border border-[#39FF14]/20' : 
                    dep.status === 'failed' ? 'bg-red-500/10 text-red-500 border border-red-500/20' : 
                    'bg-yellow-500/10 text-yellow-500 border border-yellow-500/20'
                  }`}>
                    {dep.status}
                  </span>
                </td>
                <td className="p-4 text-sm">
                  {dep.heroku_url ? (
                    <div className="flex items-center gap-2">
                      <div className="bg-[#0A0B0E] border border-white/10 px-2 py-1 text-xs text-[#8b60ed] font-mono truncate w-48 select-all" title={dep.heroku_url}>
                        {dep.heroku_url}
                      </div>
                      <button 
                        onClick={() => { navigator.clipboard.writeText(dep.heroku_url); toast.success("Copied Heroku URL!"); }}
                        className="text-slate-400 hover:text-white transition-colors p-1"
                      >
                        <Copy size={16} />
                      </button>
                      <a href={dep.heroku_url} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-[#8b60ed] transition-colors p-1">
                        <Eye size={16} />
                      </a>
                    </div>
                  ) : '-'}
                </td>
                <td className="p-4 text-sm">
                  {dep.public_url ? (
                    <div className="flex items-center gap-2">
                      <div className="bg-[#0A0B0E] border border-white/10 px-2 py-1 text-xs text-[#0078D4] font-mono truncate w-48 select-all" title={dep.public_url}>
                        {dep.public_url}
                      </div>
                      <button 
                        onClick={() => { navigator.clipboard.writeText(dep.public_url); toast.success("Copied Azure URL!"); }}
                        className="text-slate-400 hover:text-white transition-colors p-1"
                      >
                        <Copy size={16} />
                      </button>
                      <a href={dep.public_url} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-[#0078D4] transition-colors p-1">
                        <Eye size={16} />
                      </a>
                    </div>
                  ) : '-'}
                </td>
                <td className="p-4 flex items-center justify-end gap-2">
                  <button 
                    onClick={() => handleDelete(dep.id)} 
                    className="bg-red-500/10 text-red-500 border border-red-500/20 hover:bg-red-500 hover:text-white transition-colors px-3 py-1 font-bold text-xs flex items-center gap-1 uppercase tracking-wider"
                    title="Delete Resource Group & Heroku App"
                  >
                    <Trash size={14} /> Delete
                  </button>
                </td>
              </tr>
            ))}
            {deployments.length === 0 && (
              <tr>
                <td colSpan="6" className="p-8 text-center text-slate-500">No Cocktail deployments found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CocktailManageUrls;